import { IWprControlProps } from '../props/IWprControlProps';
import { IWprControlState, WprControlVisibility } from '../props/IWprControlState';
import { WprControlInfo } from '../../view/control/WprControlInfo';
import { WprBaseComponent } from './WprBaseComponent';
import { IWprControlEvents } from '../props/IWprControlEvents';
import { WprFramework } from '../../WprFramework';

/**
 * コントロール基本コンポーネント
 */
export abstract class WprBaseControlComponent<T extends IWprControlProps, U> extends WprBaseComponent<T, IWprControlState> {
	// private 変数  ------------------------------------------------------------
	private m_ControlInfo: WprControlInfo 	= null;		// コントロール情報
	private m_Events: IWprControlEvents<U>	= {};		// イベント情報
	// --------------------------------------------------------------------------

	// プロパティ  ---------------------------------------------------------------
	/** コントロール情報 */
	public get controlInfo(): WprControlInfo 	{ return this.m_ControlInfo; 	}
	/** 編集フラグ */
	public get isEdit(): boolean			 	{ return true; 					}
	/** ValueMap保持フラグ */
	public get hasValueMap(): boolean			{ return false; 				}
	// --------------------------------------------------------------------------

	// コンストラクタ  -----------------------------------------------------------
	constructor(props: T) {
		super(props);
		if (this.props.row)
			this.m_ControlInfo = this.props.view.getControlInfo(this.props.name, this.props.row.listName, this.props.row.index);
		else
			this.m_ControlInfo = this.props.view.getControlInfo(this.props.name);
		if (this.m_ControlInfo) {
			this.state = this.m_ControlInfo.getComponentState(props.row);
		}
		else {
			this.props.view.addErrorLog(`コントロール定義が登録されていません。[${this.props.name}]`);
			this.state = {
				value: '',
				disabled: false,
				style: null,
				dirty: false,
				invalid: false,
				visibility: WprControlVisibility.VISIBLE
			};
		}
	}
	// --------------------------------------------------------------------------

	// override メソッド  -------------------------------------------------------
	/**
	 * マウント通知
	 */
	public onDidMount() {
		if (this.m_ControlInfo != null) {
			this.m_ControlInfo.setComponent(this);
			if (this.props.row)
				this.setState(this.m_ControlInfo.getComponentState(this.props.row));
		}
	}
	// --------------------------------------------------------------------------

	// virtual メソッド  --------------------------------------------------------
	/**
	 * コントロール名称取得
	 * @returns コントロール名称
	 */
	public getName(): string {
		return this.props.name;
	}

	/**
	 * 選択位置リセット処理
	 */
	public resetPos(): void {
	}
	// --------------------------------------------------------------------------

	// protected メソッド  -------------------------------------------------------
	/**
	 * クラス取得
	 * @param cls 固定クラス 
	 * @returns クラス
	 */
	protected className(cls?: string): string {
		if (!this.props.className && !this.state.className && !cls)
			return cls;
		let className = '';
		if (this.props.className)
			className += this.props.className;
		if (this.state.className) {
			if (className.length > 0)
				className += ' ';
			className += this.state.className;
		}
		if (cls) {
			if (className.length > 0)
				className += ' ';
			className += cls;
		}
		return className;
	}

	/**
	 * 値取得
	 * @returns 値
	 */
	protected getValue(): any {
		return this.state.value;
	}

	/**
	 * ステータス情報抽出
	 * @param list 抽出リスト
	 * @returns ステータス情報
	 */
	protected getState(list: string[]): any {
		const rtn: any = {};
		const state: any = this.state;
		list.forEach(name => {
			rtn[name] = state[name];
		});
		return rtn;
	}

	/**
	 * プロパティ情報取得
	 * @param cls オリジナルクラス
	 * @returns プロパティ情報
	 */
	protected getProps(cls?: string): any {
		const rtn: any = {};
		rtn['name'] = this.getName();
		rtn['className'] = this.className(cls);
		const value = this.getValue();
		if (value !== undefined)
			rtn['value'] = value;
		return rtn;
	}

	/**
	 * イベント情報取得
	 * @returns イベント情報
	 */
	protected getEvents(): IWprControlEvents<U> {
		return this.m_Events;
	}

	/**
	 * クリックイベントハンドラ登録
	 * @param click クリックイベントハンドラ
	 */
	protected setClickEvent(click: (event: React.MouseEvent<U>) => void) {
		this.m_Events.onClick = click;
	}

	/**
	 * 値変更通知イベントハンドラ登録
	 * @param changeValue 値変更通知イベントハンドラ
	 */
	protected setChangeValueEvent(changeValue: (event: React.ChangeEvent<U>) => void) {
		this.m_Events.onChange = changeValue;
	}

	/**
	 * ロストフォーカスイベントハンドラ登録
	 * @param blur ロストフォーカスイベントハンドラ
	 */
	protected setBlureEvent(blur: (event: React.ChangeEvent<U>) => void) {
		this.m_Events.onBlur = blur;
	}

	/**
	 * 選択変更イベントハンドラ登録
	 * @param select 選択変更イベントハンドラ
	 */
	protected setSelectionChangeEvent(select: (event: React.ChangeEvent<U>) => void) {
		this.m_Events.onSelect = select;
	}

	/**
	 * 値を変換する
	 * @param converterName 変換名
	 * @param value 値
	 * @returns 変換後の値
	 */
	protected convertValue(converterName: string, value: string): string {
		const converter = WprFramework.getInstance().view.getConverter(converterName);
		if (converter != null )
			return converter.convert(value);
		return value;
	}
	// --------------------------------------------------------------------------

	// public メソッド  ----------------------------------------------------------
	/**
	 * 値設定処理
	 * @param value 値
	 */
	public setValue(value: any): void {
		if (this.isMount === false)
			return;
		this.setState({
			value : value
		});
	}

	/**
	 * 値更新処理
	 * @param value 値
	 */
	public updateValue(value: any): void {
		if (this.isMount === false)
			return;
		if (this.state.value !== value) {
			this.setState({
				value : value
			});
		}
	}

	/**
	 * 変更フラグ設定処理
	 * @param dirty 変更フラグ
	 */
	public setDirty(dirty: boolean): void {
		if (this.isMount === false)
			return;
		this.setState({
			dirty : dirty
		});
	}

	/**
	 * 非活性状態設定処理
	 * @param disabled 非活性状態
	 */
	public setDisabled(disabled: boolean): void {
		if (this.isMount === false)
			return;
		this.setState({
			disabled : disabled
		});
	}

	/**
	 * 読取り専用設定処理
	 * @param readOnly 読取り専用フラグ
	 */
	public setReadOnly(readOnly: boolean): void {
		if (this.isMount === false)
			return;
		this.setState({
			readOnly : readOnly
		});
	}

	/**
	 * 表示状態設定処理
	 * @param visibility 表示状態
	 * @param display 表示状態
	 */
	public setVisibility(visibility: boolean, display: boolean): void {
		if (this.isMount === false)
			return;
		let visible = WprControlVisibility.VISIBLE;
		if (display === false)
			visible = WprControlVisibility.COLLAPSED;
		else if (visibility === false)
			visible = WprControlVisibility.HIDDEN;
		this.setState({
			visibility : visible
		});
	}

	/**
	 * スタイル設定処理
	 * @param style スタイル
	 */
	public setStyle(style: any) {
		if (this.isMount === false)
			return;
		this.setState({
			style: style
		});
	}

	/**
	 * クラス設定処理
	 * @param cls クラス
	 */
	public setClass(cls: any) {
		if (this.isMount === false)
			return;
		this.setState({
			className: cls
		});
	}

	/**
	 * placeholder設定処理
	 * @param placeholder placeholder値
	 */
	public setPlaceholder(placeholder: string) {
		if (this.isMount === false)
			return;
		this.setState({
			placeholder: placeholder
		});
	}

	/**
	 * 入力エラー設定処理
	 * @param invalid 入力エラー
	 */
	public setInvalid(invalid: boolean): void {
		if (this.isMount === false)
			return;
		this.setState({
			invalid : invalid
		});
	}
	// --------------------------------------------------------------------------
}